// Sentiment analysis — until opinion data (surveys, field reports, social
// media) is connected, this measures registration sentiment: whether
// registration is speeding up or slowing down in each area, from the roll's
// daily registrations. Each area's momentum (its last 30 days' daily rate
// against the 60 days before) is compared with the national momentum:
// clearly ahead = Positive, clearly behind = Negative, otherwise Neutral.

import React, { useMemo } from 'react';
import { Box, Grid, LinearProgress, Typography } from '@mui/material';
import ForumIcon from '@mui/icons-material/Forum';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { areaName, dayLabel, useDataScope } from './data';
import { emptyIebc, IebcLevel, useRegPivot } from './agg';
import { C, Column, DataTable, fmt, fmtShort, Kpi, Panel, Section } from './ui';

const WINDOW = 90; // days of daily registrations read
const RECENT = 30; // the most recent part, compared with the 60 days before
const BAND = 10; // points either side of the national momentum that count as Neutral

type Mood = 'Positive' | 'Neutral' | 'Negative' | 'No activity';
const moodColor = (m: Mood) => ({ Positive: C.green, Neutral: C.muted, Negative: C.orange, 'No activity': C.border })[m];
const MOOD_ICON: Record<Mood, React.ReactElement> = {
  Positive: <SentimentSatisfiedAltIcon sx={{ fontSize: 16 }} />,
  Neutral: <SentimentNeutralIcon sx={{ fontSize: 16 }} />,
  Negative: <SentimentDissatisfiedIcon sx={{ fontSize: 16 }} />,
  'No activity': <SentimentNeutralIcon sx={{ fontSize: 16 }} />,
};

interface MoodRow {
  key: string;
  name: string;
  recent: number;
  previous: number;
  recentPerDay: number;
  previousPerDay: number;
  momentum: number | null; // % change in the daily rate
  relative: number | null; // momentum minus the national momentum, in points
  mood: Mood;
}

const plural = (w: string) => (w === 'County' ? 'counties' : `${w.toLowerCase()}s`);
/** Bars are capped so one surge doesn't flatten the rest; tooltips show the real figure. */
const CAP = 200;

const addDays = (d: string, n: number) => new Date(Date.parse(`${d}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);

export const SentimentSection: React.FC<{ county: string }> = ({ county }) => {
  const scope = useDataScope();
  const last = scope.last;
  const from = last ? addDays(last, -(WINDOW - 1)) : '';
  const split = last ? addDays(last, -(RECENT - 1)) : ''; // first day of the recent part
  const f = { ...emptyIebc, county };
  const rowsBy: IebcLevel = county ? 'constituency' : 'county';
  const pivot = useRegPivot(f, { preset: 'custom', from, to: last }, rowsBy, 'day', !!last);
  const levelName = county ? 'Constituency' : 'County';

  const { rows, national, daily } = useMemo(() => {
    const p = pivot.pivot;
    if (!p || !last) return { rows: [] as MoodRow[], national: null as MoodRow | null, daily: [] as { day: string; registered: number }[] };
    const isRecent = p.columns.map((c) => c >= split);
    const sums = (values: number[]) => values.reduce((a, v, j) => (isRecent[j] ? { ...a, r: a.r + v } : { ...a, p: a.p + v }), { r: 0, p: 0 });
    const momentum = (r: number, prev: number) => (prev > 0 ? ((r / RECENT) / (prev / (WINDOW - RECENT)) - 1) * 100 : null);
    const colTotals = p.columns.map((_, j) => p.rows.reduce((a, r) => a + r.values[j], 0));
    const nat = sums(colTotals);
    const natMomentum = momentum(nat.r, nat.p);
    const toRow = (key: string, s: { r: number; p: number }): MoodRow => {
      const m = momentum(s.r, s.p);
      const rel = m === null || natMomentum === null ? null : m - natMomentum;
      const mood: Mood = s.r === 0 && s.p === 0 ? 'No activity' : rel === null ? (s.r > 0 ? 'Positive' : 'Neutral') : rel > BAND ? 'Positive' : rel < -BAND ? 'Negative' : 'Neutral';
      return {
        key, name: areaName(key), recent: s.r, previous: s.p, recentPerDay: s.r / RECENT, previousPerDay: s.p / (WINDOW - RECENT),
        momentum: m, relative: rel, mood,
      };
    };
    // every day in the window, including days without registrations
    const byDay = new Map(p.columns.map((c, j) => [c, colTotals[j]]));
    const days: { day: string; registered: number }[] = [];
    for (let d = from; d <= last; d = addDays(d, 1)) days.push({ day: d, registered: byDay.get(d) ?? 0 });
    return { rows: p.rows.map((r) => toRow(r.key, sums(r.values))), national: toRow('Kenya', nat), daily: days };
  }, [pivot.pivot, last, split, from]);

  const counts = (m: Mood) => rows.filter((r) => r.mood === m).length;
  const ranked = [...rows].filter((r) => r.relative !== null).sort((a, b) => (b.relative ?? 0) - (a.relative ?? 0));
  const scopeName = areaName(county) || 'Kenya';

  const columns: Column<MoodRow>[] = [
    { key: 'name', label: levelName, value: (r) => r.name },
    { key: 'mood', label: 'Sentiment', value: (r) => r.mood },
    { key: 'recent', label: `Last ${RECENT} days`, value: (r) => r.recent, kind: 'num', color: C.green },
    { key: 'previous', label: `${WINDOW - RECENT} days before`, value: (r) => r.previous, kind: 'num' },
    { key: 'rate', label: 'Per day now', value: (r) => Math.round(r.recentPerDay), kind: 'num' },
    { key: 'momentum', label: 'Momentum %', value: (r) => (r.momentum === null ? 0 : r.momentum), kind: 'percent', total: () => national?.momentum ?? 0 },
    { key: 'relative', label: 'Vs national (pts)', value: (r) => (r.relative === null ? 0 : Math.round(r.relative)), kind: 'num', total: () => 0 },
  ];

  return (
    <Section id="sentiment" icon={<ForumIcon />} title="Sentiment analysis" accent={C.cyan}
      subtitle={`${scopeName} · registration sentiment · last ${WINDOW} days to date`}>
      <Box sx={{ mb: 2, p: 1.25, borderRadius: 1, border: `1px solid ${C.cyan}55`, backgroundColor: `${C.cyan}12`, fontSize: '0.75rem', color: C.text }}>
        <b>Registration sentiment.</b> There is no opinion data yet (surveys, field reports, social media), so this page reads the mood
        from registration activity: each {levelName.toLowerCase()}'s daily registrations over the last {RECENT} days against the {WINDOW - RECENT} days
        before, compared with the national trend. More than {BAND} points ahead of the nation is <b style={{ color: C.green }}>Positive</b>,
        more than {BAND} behind is <b style={{ color: C.orange }}>Negative</b>, otherwise <b>Neutral</b>.
      </Box>
      {pivot.fetching && <LinearProgress sx={{ height: 2, mb: 1, backgroundColor: 'transparent', '& .MuiLinearProgress-bar': { backgroundColor: C.cyan } }} />}
      {pivot.error && <Typography sx={{ color: C.orange, fontSize: '0.8rem', mb: 1 }}>{(pivot.error as any)?.message || 'Could not load registrations.'}</Typography>}

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}><Kpi label="Positive" value={counts('Positive')} color={C.green} sub={`${plural(levelName)} gaining pace`} /></Grid>
        <Grid item xs={6} md={3}><Kpi label="Neutral" value={counts('Neutral')} color={C.text} sub="moving with the nation" /></Grid>
        <Grid item xs={6} md={3}><Kpi label="Negative" value={counts('Negative')} color={C.orange} sub={`${plural(levelName)} losing pace`} /></Grid>
        <Grid item xs={6} md={3}><Kpi label={`${scopeName} momentum`} color={C.cyan}
          value={national?.momentum == null ? '—' : `${national.momentum >= 0 ? '+' : ''}${national.momentum.toFixed(0)}%`}
          sub={national ? `${fmt(national.recentPerDay)} a day now vs ${fmt(national.previousPerDay)} before` : ''} /></Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} lg={6}>
          <Panel title={`Daily registrations · ${scopeName}`} sx={{ height: 420 }}>
            <Box sx={{ height: 360, p: 1 }}>
              <ResponsiveContainer>
                <AreaChart data={daily.map((d) => ({ ...d, label: d.day === last ? 'To date' : dayLabel(d.day) }))} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={{ fill: C.muted, fontSize: 11 }} minTickGap={24} interval="preserveStartEnd" />
                  <YAxis tickFormatter={fmtShort} tick={{ fill: C.muted, fontSize: 11 }} width={48} />
                  <Tooltip contentStyle={{ backgroundColor: C.card, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12 }}
                    formatter={(v: number) => [v.toLocaleString(), 'Registered']} />
                  {split && <ReferenceLine x={dayLabel(split)} stroke={C.cyan} strokeDasharray="4 4" label={{ value: `last ${RECENT} days`, fill: C.cyan, fontSize: 10, position: 'insideTopRight' }} />}
                  <Area type="monotone" dataKey="registered" stroke={C.cyan} strokeWidth={2} fill={C.cyan} fillOpacity={0.15} />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </Panel>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Panel title={`Momentum vs national, by ${levelName.toLowerCase()} (points; bars capped at ±${CAP})`} sx={{ height: 420 }}>
            <Box sx={{ height: 360, p: 1, overflowY: 'auto' }}>
              <Box sx={{ height: Math.max(340, ranked.length * 18) }}>
                <ResponsiveContainer>
                  <BarChart data={ranked.map((r) => ({ name: r.name, bar: Math.max(-CAP, Math.min(CAP, Math.round(r.relative ?? 0))), relative: Math.round(r.relative ?? 0), mood: r.mood }))} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                    <CartesianGrid stroke={C.border} strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" domain={[-CAP, CAP]} tick={{ fill: C.muted, fontSize: 11 }} tickFormatter={(v) => `${v > 0 ? '+' : ''}${v}${Math.abs(v) === CAP ? '+' : ''}`} />
                    <YAxis type="category" dataKey="name" width={110} tick={{ fill: C.text, fontSize: 10 }} interval={0} />
                    <ReferenceLine x={0} stroke={C.muted} />
                    <Tooltip contentStyle={{ backgroundColor: C.card, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12 }}
                      cursor={{ fill: C.faint }} formatter={(_v: number, _n, p: any) => [`${p.payload.relative > 0 ? '+' : ''}${p.payload.relative} pts vs national (${p.payload.mood})`, 'Momentum']} />
                    <Bar dataKey="bar" radius={2}>
                      {ranked.map((r) => <Cell key={r.key} fill={moodColor(r.mood)} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          </Panel>
        </Grid>
      </Grid>

      <Panel title={`Sentiment by ${levelName.toLowerCase()}`} sx={{ height: 480 }}>
        <DataTable<MoodRow> rows={rows} columns={columns} rowKey={(r) => r.key} maxHeight={390} fileName={`sentiment-${scopeName}`}
          searchPlaceholder={`Search ${levelName.toLowerCase()}…`} emptyText={pivot.loading ? 'Loading…' : 'No rows'} />
      </Panel>
      <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
        {(['Positive', 'Neutral', 'Negative'] as Mood[]).map((m) => (
          <Box key={m} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: moodColor(m), fontSize: '0.72rem' }}>{MOOD_ICON[m]}{m}</Box>
        ))}
      </Box>
    </Section>
  );
};
